import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import '@/App.css';

import Navbar from '@/components/Navbar';
import ScrollProgress from '@/components/ScrollProgress';
import NoiseGrain from '@/components/NoiseGrain';
import ChatWidget from '@/components/ChatWidget';
import AvailabilityPill from '@/components/AvailabilityPill';
import LoadingScreen from '@/components/LoadingScreen';
import Cursor from '@/components/Cursor';
import PageTransition from '@/components/PageTransition';

const Home = lazy(() => import('@/pages/Home'));
const Admin = lazy(() => import('@/pages/Admin'));
const ServicesPage = lazy(() => import('@/pages/ServicesPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const EstimatePage = lazy(() => import('@/pages/EstimatePage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const JournalPage = lazy(() => import('@/pages/JournalPage'));
const ArticlePage = lazy(() => import('@/pages/ArticlePage'));

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#050608' }}>
      <svg width="48" height="48" viewBox="0 0 80 80" className="animate-pulse">
        <path d="M10 5 L10 75" stroke="#E8A020" strokeWidth="3" fill="none" strokeLinecap="round">
          <animate attributeName="stroke-dasharray" from="0 70" to="70 0" dur="1.5s" repeatCount="indefinite" />
        </path>
        <path d="M70 5 L70 75" stroke="#E8A020" strokeWidth="3" fill="none" strokeLinecap="round">
          <animate attributeName="stroke-dasharray" from="0 70" to="70 0" dur="1.5s" repeatCount="indefinite" begin="0.3s" />
        </path>
        <path d="M10 40 L70 40" stroke="#E8A020" strokeWidth="3" fill="none" strokeLinecap="round">
          <animate attributeName="stroke-dasharray" from="0 60" to="60 0" dur="1.5s" repeatCount="indefinite" begin="0.6s" />
        </path>
      </svg>
    </div>
  );
}

function HolidayBanner() {
  const [notice, setNotice] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const n = localStorage.getItem('holistic_notice');
    if (n) setNotice(n);
  }, []);

  if (!notice || dismissed) return null;

  return (
    <div data-testid="holiday-banner" className="fixed top-[72px] left-0 right-0 z-[199] flex items-center justify-center gap-4 px-4 py-2.5 text-center" style={{ background: 'rgba(232,160,32,0.08)', borderBottom: '1px solid rgba(232,160,32,0.15)' }}>
      <p className="font-sans font-medium text-[13px]" style={{ color: 'rgba(232,160,32,0.9)' }}>
        &#9888; {notice} Please call <a href="tel:+251911146950" className="underline" style={{ color: '#E8A020' }}>+251 91 114 6950</a> to confirm availability.
      </p>
      <button onClick={() => setDismissed(true)} className="font-sans text-xs" style={{ color: 'rgba(232,160,32,0.5)' }}>&times;</button>
    </div>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname === '/admin';

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Cursor />}
      {!isAdmin && <ScrollProgress />}
      {!isAdmin && <Navbar />}
      {!isAdmin && <HolidayBanner />}
      <div className="blueprint-grid-overlay" />
      <NoiseGrain />

      <Suspense fallback={<LoadingFallback />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<PageTransition><Home /></PageTransition>} />
            <Route path="/services" element={<PageTransition><ServicesPage /></PageTransition>} />
            <Route path="/projects" element={<PageTransition><ProjectsPage /></PageTransition>} />
            <Route path="/estimate" element={<PageTransition><EstimatePage /></PageTransition>} />
            <Route path="/about" element={<PageTransition><AboutPage /></PageTransition>} />
            <Route path="/contact" element={<PageTransition><ContactPage /></PageTransition>} />
            <Route path="/journal" element={<PageTransition><JournalPage /></PageTransition>} />
            <Route path="/journal/:slug" element={<PageTransition><ArticlePage /></PageTransition>} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </AnimatePresence>
      </Suspense>

      {!isAdmin && <AvailabilityPill />}
      {!isAdmin && <ChatWidget />}
    </>
  );
}

function App() {
  const [loaded, setLoaded] = useState(() => sessionStorage.getItem('holistic_loaded') === '1');

  // Initialize Lenis smooth scroll
  useEffect(() => {
    let lenis;
    const initLenis = async () => {
      try {
        const Lenis = (await import('@studio-freight/lenis')).default;
        lenis = new Lenis({ lerp: 0.075, smoothWheel: true });
        function raf(time) {
          lenis.raf(time);
          requestAnimationFrame(raf);
        }
        requestAnimationFrame(raf);
      } catch (e) {
        console.warn('Lenis not available:', e);
      }
    };
    if (loaded) initLenis();
    return () => { if (lenis) lenis.destroy(); };
  }, [loaded]);

  if (!loaded) {
    return <LoadingScreen onComplete={() => setLoaded(true)} />;
  }

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
